import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [accessType, setAccessType] = useState("institution");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loginEmail, signupEmail, accessConfig } = useAuth();
  const navigate = useNavigate();

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter both email and password.");
      toast.error("Please enter both email and password.");
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      if (mode === "login") {
        const user = await loginEmail(email, password);
        toast.success("Welcome back.");
        navigate(user.access_required && !user.access_granted ? "/onboarding" : "/dashboard");
      } else {
        if (!accessConfig.allow_independent_signups && !accessCode.trim()) {
          setError("Enter your class or institution code to begin.");
          toast.error("Enter your class or institution code to begin.");
          return;
        }
        const user = await signupEmail(email, password, name, {
          access_type: accessCode.trim() ? accessType : undefined,
          access_code: accessCode.trim() || undefined,
        });
        toast.success("Your forge is lit. Welcome.");
        navigate(user.access_required && !user.access_granted ? "/onboarding" : "/dashboard");
      }
    } catch (err) {
      const detail = err?.code === "ECONNABORTED"
        ? "Login timed out. Check that the backend is running and try again."
        : err?.response?.data?.detail || "Authentication failed.";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100 grain">
      <Header minimal />
      <div className="max-w-md mx-auto px-6 pt-16 sm:pt-24 pb-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">{mode === "login" ? "Return to the forge" : "Enter the forge"}</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2" data-testid="auth-title">
          {mode === "login" ? "Welcome back." : "Begin your study."}
        </h1>
        <p className="text-sm text-forge-100/60 mb-10">
          {mode === "login" ? "Sign in to continue forging ideas." : "Create an account to track your intellectual growth."}
        </p>

        <button
          onClick={handleGoogle}
          className="w-full flex items-center justify-center gap-3 px-5 py-3 border border-white/15 hover:border-ember-500 hover:text-ember-400 rounded-sm transition-all mb-6"
          data-testid="auth-google-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.5 1.1 7.5 2.8l5.7-5.7C33.6 6.2 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 12.5 24 12.5c2.9 0 5.5 1.1 7.5 2.8l5.7-5.7C33.6 6.2 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.1 0 9.8-2 13.3-5.2l-6.2-5.1C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.5 39.7 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.8-3.6 5.1l6.2 5.1C40 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/>
          </svg>
          <span className="text-sm tracking-wide">Continue with Google</span>
        </button>

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-forge-100/40">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="border border-ember-500/35 bg-ember-500/10 text-ember-200 px-4 py-3 rounded-sm text-sm leading-relaxed" role="alert">
              {error}
            </div>
          )}
          {mode === "signup" && (
            <>
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
                data-testid="auth-name-input"
              />
              <div className="border border-white/10 bg-forge-800/45 rounded-sm p-4 space-y-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-ember-500">Pilot Access</div>
                <p className="text-xs text-forge-100/55">
                  Enter your class or institution code to begin.
                  {accessConfig.allow_independent_signups ? " Independent learners may leave this blank." : ""}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["institution", "Institution"],
                    ["class", "Class"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setAccessType(value)}
                      className={`px-3 py-2 border rounded-sm text-xs uppercase tracking-[0.2em] transition-colors ${accessType === value ? "border-ember-500 text-ember-400 bg-ember-500/8" : "border-white/10 text-forge-100/55 hover:border-ember-500/50"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder={accessType === "institution" ? "FLINTLIBRARY" : "SMITH8"}
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 bg-forge-900 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors font-mono tracking-[0.14em]"
                  required={!accessConfig.allow_independent_signups}
                />
              </div>
            </>
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
            required
            data-testid="auth-email-input"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors"
            required
            minLength={6}
            data-testid="auth-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full px-5 py-3 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
            data-testid="auth-submit"
          >
            {loading ? "Working…" : (mode === "login" ? "Sign in" : "Create account")}
          </button>
        </form>

        <div className="mt-8 text-center text-sm text-forge-100/60">
          {mode === "login" ? (
            <>New here?{" "}
              <button onClick={() => setMode("signup")} className="text-ember-400 hover:text-ember-500 underline-offset-4 hover:underline" data-testid="auth-toggle-signup">Create an account</button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button onClick={() => setMode("login")} className="text-ember-400 hover:text-ember-500 underline-offset-4 hover:underline" data-testid="auth-toggle-login">Sign in</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
